import React from "react";

/**
 * The PLCB licensee invoice, box for box as it is printed: order #, pickup
 * date and store, gross, less the licensee discount, = taxable, + sales tax,
 * = total paid. Paula, 20 Oct 2026: the sales tax on these is claimable on
 * the PA-3, but only if it is on record -- so it is typed in here, not left
 * on the paper.
 *
 * Kept as strings so a half-typed "1." survives; the parent turns them into
 * numbers when it saves.
 */
export interface PlcbInvoiceForm {
  order_number: string;
  pickup_date: string;
  store_number: string;
  gross_amount: string;
  licensee_discount: string;
  sales_tax: string;
  total_paid: string;
}

export interface PlcbInvoiceRecord {
  order_number: string | null;
  pickup_date: string;
  store_number: string | null;
  gross_amount: number | string;
  licensee_discount: number | string;
  sales_tax: number | string;
  total_paid: number | string;
  claim_status?: string | null;
}

export function blankPlcbForm(pickupDate?: string): PlcbInvoiceForm {
  return {
    order_number: "", pickup_date: pickupDate || new Date().toISOString().slice(0, 10), store_number: "",
    gross_amount: "", licensee_discount: "", sales_tax: "", total_paid: "",
  };
}

export function plcbFormFrom(rec: PlcbInvoiceRecord | null | undefined, pickupDate?: string): PlcbInvoiceForm {
  if (!rec) return blankPlcbForm(pickupDate);
  const s = (v: number | string | null | undefined) => (v === null || v === undefined ? "" : String(Number(v).toFixed(2)));
  return {
    order_number: rec.order_number || "",
    pickup_date: String(rec.pickup_date).slice(0, 10),
    store_number: rec.store_number || "",
    gross_amount: s(rec.gross_amount),
    licensee_discount: s(rec.licensee_discount),
    sales_tax: s(rec.sales_tax),
    total_paid: s(rec.total_paid),
  };
}

const num = (v: string): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};

/** The figures the parent needs: taxable, tax, and whether the paper adds up. */
export function plcbMath(f: PlcbInvoiceForm) {
  const taxable = Math.round((num(f.gross_amount) - num(f.licensee_discount)) * 100) / 100;
  const tax = num(f.sales_tax);
  const adds = Math.round((taxable + tax) * 100) / 100;
  const paid = num(f.total_paid);
  const filled = f.gross_amount.trim() !== "" && f.sales_tax.trim() !== "" && f.total_paid.trim() !== "";
  return { taxable, tax, adds, paid, filled, ties: filled && Math.abs(adds - paid) <= 0.01 };
}

export function plcbPayload(f: PlcbInvoiceForm) {
  return {
    order_number: f.order_number.trim() || null,
    pickup_date: f.pickup_date,
    store_number: f.store_number.trim() || null,
    gross_amount: num(f.gross_amount),
    licensee_discount: num(f.licensee_discount),
    sales_tax: num(f.sales_tax),
    total_paid: num(f.total_paid),
  };
}

const usd = (n: number) => (n < 0 ? "-$" : "$") + Math.abs(n).toFixed(2);

const inputStyle: React.CSSProperties = {
  border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)",
  background: "var(--rbw-bg-card)", width: "130px", textAlign: "right",
};
const cellLabel: React.CSSProperties = { padding: "4px 12px 4px 0", color: "var(--rbw-navy)" };
const cellValue: React.CSSProperties = { padding: "4px 0", textAlign: "right" };

export default function PlcbInvoiceFields({
  value,
  onChange,
  disabled = false,
}: {
  value: PlcbInvoiceForm;
  onChange: (next: PlcbInvoiceForm) => void;
  disabled?: boolean;
}) {
  const set = (k: keyof PlcbInvoiceForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [k]: e.target.value });
  const m = plcbMath(value);

  const text = (label: string, k: keyof PlcbInvoiceForm, type = "text", placeholder = "") => (
    <tr>
      <td style={cellLabel}>{label}</td>
      <td style={cellValue}>
        <input type={type} value={value[k]} onChange={set(k)} disabled={disabled}
          className="px-3 py-1.5 rounded-lg outline-none" style={inputStyle} placeholder={placeholder} />
      </td>
    </tr>
  );
  const money = (label: string, k: keyof PlcbInvoiceForm, opts: { bold?: boolean; bad?: boolean } = {}) => (
    <tr>
      <td style={{ ...cellLabel, fontWeight: opts.bold ? 700 : 400 }}>{label}</td>
      <td style={cellValue}>
        <input type="number" step="0.01" value={value[k]} onChange={set(k)} disabled={disabled}
          className="px-3 py-1.5 rounded-lg outline-none"
          style={{ ...inputStyle, border: opts.bad ? "2px solid #C53030" : inputStyle.border }} placeholder="0.00" />
      </td>
    </tr>
  );

  return (
    <div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontVariantNumeric: "tabular-nums" }}>
        <tbody>
          {text("PLCB order #", "order_number", "text", "188987205")}
          {text("Pickup date", "pickup_date", "date")}
          {text("Store #", "store_number", "text", "3690")}
          {money("Gross", "gross_amount")}
          {money("- Licensee discount", "licensee_discount")}
          <tr>
            <td style={{ ...cellLabel, fontWeight: 600 }}>= Taxable</td>
            <td style={{ ...cellValue, fontWeight: 600, color: "var(--rbw-navy)", paddingRight: 14 }}>{usd(m.taxable)}</td>
          </tr>
          {money("+ Sales tax", "sales_tax")}
          {money("= Total paid", "total_paid", { bold: true, bad: m.filled && !m.ties })}
        </tbody>
      </table>
      {m.filled && !m.ties && (
        <div className="text-sm mt-2" style={{ color: "#C53030", fontWeight: 600 }}>
          Does not add up: {usd(m.taxable)} + {usd(m.tax)} tax = {usd(m.adds)}, not {usd(m.paid)}. Check the paper.
        </div>
      )}
      {m.ties && (
        <div className="text-sm mt-2" style={{ color: "#2D8A4E", fontWeight: 600 }}>
          Adds up. {usd(m.tax)} of sales tax goes on the next sales tax return as a credit.
        </div>
      )}
    </div>
  );
}
